/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { ChangeEvent, CSSProperties } from 'react';
import { Controller } from 'react-hook-form';
import MuiTextField from '@mui/material/TextField';
import { useTheme } from 'styled-components';
import FieldErrorMessage from 'shared/Error';

import { Wrapper, HelperText } from './styles';

export interface ITextArea {
  id: string;
  name: string;
  label: string;
  control?: any;
  value?: string;
  defaultValue?: string;
  rows?: number;
  resize?: 'none' | 'both' | 'horizontal' | 'vertical';
  maxLength?: number;
  disabled?: boolean;
  readOnly?: boolean;
  helperText?: string;
  justifyContentError?: 'flex-start' | 'center' | 'flex-end';
  errorRowOnlyWhenInvalid?: boolean;
  error?: { message?: string };
  className?: string;
  style?: CSSProperties;
  onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void;
}

const TextArea: React.FC<ITextArea> = ({
  id,
  name,
  label,
  control,
  value,
  defaultValue = '',
  rows = 5,
  resize = 'vertical',
  maxLength,
  disabled = false,
  readOnly = false,
  helperText,
  justifyContentError = 'flex-start',
  errorRowOnlyWhenInvalid = false,
  error,
  className,
  style,
  onChange,
}) => {
  const theme = useTheme();
  const hasError = !!error;

  const borderColor = hasError ? theme.colors.error : theme.colors.disabled;
  const activeBorderColor = hasError ? theme.colors.error : theme.colors.primary;
  const contentColor = hasError ? theme.colors.error : theme.colors.primary;
  const borderWidth = hasError ? 2 : 1;

  const renderField = (fieldValue: string, fieldOnChange: (value: string) => void, fieldOnBlur?: () => void) => (
    <MuiTextField
      id={id}
      multiline
      fullWidth
      variant="outlined"
      label={label}
      value={fieldValue ?? ''}
      rows={rows}
      disabled={disabled}
      error={hasError}
      onChange={(event) => {
        if (disabled || readOnly) return;
        fieldOnChange(event.target.value);
        onChange?.(event as ChangeEvent<HTMLTextAreaElement>);
      }}
      onBlur={fieldOnBlur}
      InputProps={{
        readOnly,
        inputProps: {
          maxLength,
          'data-testid': `${id}_input`,
        },
      }}
      sx={{
        width: '100%',
        '& .MuiOutlinedInput-root': {
          borderRadius: '4px',
          padding: '15px',
          fontFamily: "'Roboto', sans-serif",
          fontSize: 14,
          fontWeight: hasError ? 500 : 300,
          color: contentColor,
          backgroundColor: theme.colors.white,
          '& fieldset': {
            borderColor,
            borderWidth,
          },
          '&:hover fieldset': {
            borderColor: activeBorderColor,
            borderWidth,
          },
          '&.Mui-focused fieldset': {
            borderColor: activeBorderColor,
            borderWidth,
          },
          '&.Mui-disabled': {
            backgroundColor: theme.colors.background,
            '& fieldset': {
              borderColor: theme.colors.disabled,
            },
          },
          '& .MuiOutlinedInput-notchedOutline legend': {
            textTransform: 'uppercase',
            '& > span': {
              paddingLeft: '5px',
              paddingRight: '5px',
            },
          },
        },
        // Notch com teto de 90% só com label ativo (foco ou valor); no estado normal a borda fica inteira
        '& .MuiInputLabel-shrink ~ .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline legend': {
          maxWidth: '90% !important',
        },
        '& .MuiInputBase-inputMultiline': {
          resize,
          boxSizing: 'border-box',
        },
        '& .MuiOutlinedInput-input': {
          color: `${contentColor} !important`,
          WebkitTextFillColor: disabled ? theme.colors.disabled : undefined,
        },
        '& .MuiInputLabel-root': {
          fontFamily: "'Roboto', sans-serif",
          fontWeight: 500,
          fontSize: 14,
          textTransform: 'uppercase',
          color: hasError ? theme.colors.error : theme.colors.primary,
          maxWidth: '90%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          // Compensa o scale(0.75) do shrink para o teto visual continuar em ~90%
          '&.MuiInputLabel-shrink': {
            maxWidth: 'calc(90% / 0.75)',
          },
          '&.Mui-focused': {
            color: activeBorderColor,
          },
          '&.Mui-disabled': {
            color: theme.colors.disabled,
          },
        },
      }}
      InputLabelProps={{
        title: label,
      }}
      className={className}
      style={style}
    />
  );

  return (
    <Wrapper>
      {control ? (
        <Controller
          name={name}
          control={control}
          defaultValue={defaultValue}
          render={({ onChange: fieldOnChange, onBlur, value: fieldValue }) =>
            renderField(fieldValue, fieldOnChange, onBlur)
          }
        />
      ) : (
        renderField(value ?? defaultValue, (nextValue) =>
          onChange?.({ target: { value: nextValue } } as ChangeEvent<HTMLTextAreaElement>),
        )
      )}

      {(!errorRowOnlyWhenInvalid || hasError) && (
        <FieldErrorMessage
          id={`${id}_error`}
          enable={hasError}
          message={error?.message}
          justifyContent={justifyContentError}
        />
      )}

      {!hasError && helperText && <HelperText>{helperText}</HelperText>}
    </Wrapper>
  );
};

export default TextArea;
